// Secure Supabase service using server-side API routes
import { Album } from '../types/album';
import { encryptionService } from '../utils/encryption';

class SecureSupabaseService {
  private baseUrl = '';

  constructor() {
    // Use current domain in production, localhost in development
    this.baseUrl = typeof window !== 'undefined' 
      ? window.location.origin 
      : '';
  }

  async initialize(): Promise<void> {
    // No client-side initialization needed
    console.log('Secure Supabase service initialized');
  }

  // Album operations
  async getAllAlbums(): Promise<Album[]> {
    const response = await fetch(`${this.baseUrl}/api/albums`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch albums: ${response.statusText}`);
    }
    
    const { albums } = await response.json();
    return albums.map(this.convertDatabaseToAlbum);
  }

  async getAlbum(id: string): Promise<Album | null> {
    const response = await fetch(`${this.baseUrl}/api/albums/${id}`);
    
    if (response.status === 404) {
      return null;
    }
    
    if (!response.ok) {
      throw new Error(`Failed to fetch album: ${response.statusText}`);
    }
    
    const { album } = await response.json();
    return this.convertDatabaseToAlbum(album);
  }

  async saveAlbum(album: Album): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/albums`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ album }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Failed to save album: ${error.error || response.statusText}`);
    }
  }

  async updateAlbum(album: Album): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/albums/${album.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ album }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Failed to update album: ${error.error || response.statusText}`);
    }
  }

  async deleteAlbum(id: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/albums/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Failed to delete album: ${error.error || response.statusText}`);
    }
  }

  // Image operations
  async getImage(albumId: string): Promise<Blob | null> {
    const response = await fetch(`${this.baseUrl}/api/images/${albumId}`);
    
    if (response.status === 404) {
      return null;
    }
    
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }
    
    const { imageData } = await response.json();
    
    // Convert base64 back to Blob
    try {
      const binaryString = atob(imageData.split(',')[1]);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new Blob([bytes]);
    } catch (error) {
      console.error('Failed to convert image data:', error);
      return null;
    }
  }

  async saveImage(albumId: string, imageBlob: Blob): Promise<void> {
    // Convert Blob to base64
    const reader = new FileReader();
    const imageData = await new Promise<string>((resolve, reject) => {
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(imageBlob);
    });

    const response = await fetch(`${this.baseUrl}/api/images/${albumId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ imageData }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Failed to save image: ${error.error || response.statusText}`);
    }
  }

  async deleteImage(albumId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/images/${albumId}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Failed to delete image: ${error.error || response.statusText}`);
    }
  }

  // Settings operations
  async getSettings(): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/settings`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch settings: ${response.statusText}`);
    }
    
    const { settings } = await response.json();
    
    // Decrypt sensitive fields
    if (settings?.discogs_token) {
      try {
        settings.discogs_token = await encryptionService.decrypt(settings.discogs_token);
      } catch (error) {
        console.warn('Failed to decrypt discogs_token, treating as unencrypted');
      }
    }
    
    return settings || {};
  }

  async updateSettings(updates: any): Promise<void> {
    // Encrypt sensitive fields before sending
    const encryptedUpdates = { ...updates };
    
    if (updates.discogs_token) {
      try {
        encryptedUpdates.discogs_token = await encryptionService.encrypt(updates.discogs_token);
      } catch (error) {
        console.error('Failed to encrypt discogs_token:', error);
        throw new Error('Failed to encrypt sensitive data');
      }
    }
    
    const response = await fetch(`${this.baseUrl}/api/settings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates: encryptedUpdates }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Failed to update settings: ${error.error || response.statusText}`);
    }
  }

  // Migration method (no-op for secure service)
  async migrateToMultipleArtists(): Promise<void> {
    console.log('Migration not needed for secure service');
  }

  // Compatibility method for existing code
  async clear(): Promise<void> {
    console.warn('Clear operation not supported in secure service');
  }

  // Compatibility method for existing code  
  async export(): Promise<string> {
    const albums = await this.getAllAlbums();
    return JSON.stringify({ albums }, null, 2);
  }

  // Database to Album conversion
  private convertDatabaseToAlbum(dbAlbum: any): Album {
    return {
      id: dbAlbum.id,
      artist: dbAlbum.artist,
      artists: dbAlbum.artists || [dbAlbum.artist],
      title: dbAlbum.title,
      year: dbAlbum.year,
      releaseYear: dbAlbum.release_year,
      coverImageBlob: undefined, // Will be loaded separately
      coverImageUrl: dbAlbum.cover_image_url,
      genres: dbAlbum.genres || [],
      isVariousArtists: dbAlbum.is_various_artists || false,
      discogsId: dbAlbum.discogs_id,
      masterId: dbAlbum.master_id,
      shelfPosition: dbAlbum.shelf_position || { section: 0, estimatedPosition: 0, totalInSection: 0 },
      sortKey: dbAlbum.sort_key || '',
      customSortName: dbAlbum.custom_sort_name,
      format: dbAlbum.format,
      tracklist: dbAlbum.tracklist,
      syncMetadata: dbAlbum.sync_metadata,
      artistCountry: dbAlbum.artist_country,
      artistCountryName: dbAlbum.artist_country_name,
      language: dbAlbum.language,
      languageName: dbAlbum.language_name,
    };
  }
}

export const secureSupabaseService = new SecureSupabaseService();